import React, {
 useEffect, useState, useRef, Fragment
} from 'react';
import { RootState } from 'data/root.reducers';
import { SnmpDestination, SnmpDestPrivileges } from 'data/snmp/snmp.types';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as actions from 'data/snmp/snmp.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/snmp/snmp.selectors';
import { snmpDestColumnDefs } from 'routes/SnmpDestination/SnmpDestination.types';
import { AppToaster } from 'shared/toaster';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo
} from 'shared/userAudit/audit-constants';
import { AuditDetails, WithAuditDetails } from 'data/types';
import adaptGridRowData from 'routes/SnmpDestination/utils/snmpDestList.adapter';
import SnmpDestListGrid from './components/SnmpDestGrid';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  deliveryFunctionId: state.global.selectedDF,
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  selectedSnmpDest: selectors.getSelectedSnmpDest(state),
  data: selectors.getSnmpDests(state)
});

const mapDispatch = {
  goToSnmpDestCreate: actions.goToSnmpDestCreate,
  goToSnmpDestEdit: actions.goToSnmpDestEdit,
  goToSnmpDestView: actions.goToSnmpDestView,
  goToSmnpDestList: actions.goToSmnpDestList,
  getSnmpDests: actions.getSnmpDests,
  deleteSnmpDest: actions.deleteSnmpDest,
  selectSnmpDest: actions.selectSnmpDest
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpDestList = ({
  privileges,
  isAuditEnabled,
  userId,
  userName,
  history,
  data,
  beforeLogout,
  deliveryFunctionId,
  selectedSnmpDest,
  goToSnmpDestCreate,
  goToSnmpDestEdit,
  goToSnmpDestView,
  goToSmnpDestList,
  getSnmpDests,
  deleteSnmpDest,
  selectSnmpDest
}: Props): JSX.Element => {
  const [gridRowData, setGridRowData] = useState<SnmpDestination[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(snmpDestColumnDefs);

  useEffect(() => {
    getSnmpDests(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getSnmpDests]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
    const gridRowData = adaptGridRowData(data);
    setGridRowData(gridRowData);
  }, [data]);

  const handleRefresh = () => getSnmpDests(deliveryFunctionId);

  const handleCreateNew = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    goToSnmpDestCreate(history);
  };

  const handleDeleteSnmpDest = () => {
    const snmpUserId = selectedSnmpDest?.id;
    if (!snmpUserId) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        snmpUserId,
        AuditService.SNMP_DEST,
        AuditActionType.SNMP_DEST_DELETE
      );
      body.auditDetails = auditDetails as AuditDetails;

      deleteSnmpDest(
        deliveryFunctionId,
        body,
        selectedSnmpDest as SnmpDestination
      );
    }
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectSnmpDest(event.api.getSelectedRows()[0]);
  };

  const handleGoToEdit = () => goToSnmpDestEdit(history);

  const handleGoToView = () => goToSnmpDestView(history);

  const hasPrivilegeToView = privileges.includes(SnmpDestPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(SnmpDestPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(SnmpDestPrivileges.Delete);

  return (
    <Fragment>
      <SnmpDestListGrid
        data={gridRowData}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedSnmpDest={selectedSnmpDest}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteSnmpDest}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(SnmpDestList));
